import type { EditorialToutWithImageBlock } from '@/@types/content';
import Image from '@/components/cms/Image';
import RichText from '@/components/cms/RichText';
import Text from '@/components/cms/Text';
import Button from '@/components/ui/Buttons/Button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { isEditOrPreviewMode } from '@/lib/editModeHelpers';
import { isExternalUrl } from '@/utils/helpers';
import classnames from 'classnames';
import Block from '../cms/Block';
import VimeoVideo from '../ui/VimeoVimeo';
import ResponsiveImage from '../ui/ResponsiveImage';
import type { RequestAQuoteModalBlock as RequestAQuoteModalBlockType } from '@/@types/content';
import RequestAQuoteModalBlock from '@/components/blocks/RequestAQuoteModalBlock';
import VisibilityObserver from '../ui/VisibilityObserver';
import { useCallback, useEffect, useState } from 'react';
import { trackSelectEditorialEvent, trackViewEditorialEvent } from '@/analytics/editorialTout';
import { LinkItem } from '@/@types/cms';
import { useAuth } from 'react-oidc-context';
import { getOIDCState } from '@/auth/oidcConfig';

enum Layout {
  MediaLeft = 'media-left',
  MediaRight = 'media-right'
}

enum ImagePlacement {
  AboveText = 'above-text',
  BelowText = 'below-text'
}

enum LinkStyle {
  Primary = 'primary',
  Secondary = 'secondary'
}

interface EditorialToutWithImageBlockProps extends EditorialToutWithImageBlock {
  smallImage: boolean;
  outerHeading?: string;
  onPrimaryButtonClick?: () => void;
  eyebrowSize?: 'normal' | 'small';
}

const EditorialToutWithImageBlock: React.FC<EditorialToutWithImageBlockProps> = ({
  contentLink,
  layout = Layout.MediaLeft,
  image,
  roundedCorners = false,
  videoID,
  logo,
  eyebrowText,
  eyebrowSize = "small",
  heading,
  copy,
  primaryLink,
  link1Style,
  secondaryLink,
  link2Style,
  checkListRed = false,
  smallImage = false,
  aspectRadioLayout = '1:1',
  grayBackground = false,
  outerHeading,
  requestQuoteFlowtComponent,
  imagePlacement = ImagePlacement.AboveText,
  contentType,
  requireAuthOnLinks,
  link1RenderAsButton,
  onPrimaryButtonClick
}) => {
  const auth = useAuth();
  const editMode = isEditOrPreviewMode();
  const screenSizeXsOnly = useMediaQuery(ScreenSizeQueries.xsOnly);
  const requestAQuoteModalBlockProps =
    requestQuoteFlowtComponent?.[0] as RequestAQuoteModalBlockType;

  // TODO: Temporary flag, update when a ticket is created to handle alternative layout
  const alternativeLayout = false;

  const flexClasses = classnames(
    'flex flex-col items-center gap-4',
    {
      'sm:gap-12': !alternativeLayout,
      'sm:grid sm:min-h-[360px] sm:grid-cols-2 sm:gap-6': alternativeLayout,
      'md:gap-10': !alternativeLayout,
      'lg:gap-[133px]': !alternativeLayout
    },
    {
      'md:flex-row': layout === Layout.MediaLeft,
      'md:flex-row-reverse': layout === Layout.MediaRight
    },
    {
      'flex-col-reverse': imagePlacement === ImagePlacement.BelowText
    },
    {
      'overflow-hidden rounded-lg': alternativeLayout
    },
    {
      'bg-gray-feather': grayBackground
    }
  );

  const smallImageClasses = classnames('w-1/2 self-start', 'md:w-2/5');

  const imageColumnClasses = classnames('w-full', {
    'lg:w-1/2 lg:flex-[0_0_calc(50%-12px)]': !alternativeLayout,
    'sm:featured-content-image sm:h-full': alternativeLayout,
    'sm:order-1': alternativeLayout && layout === Layout.MediaRight
  });

  const infoColumnClasses = classnames('flex w-full flex-col gap-8', {
    'p-8': alternativeLayout,
    'lg:p-16': alternativeLayout,
    'lg:gap-8': !smallImage
  });

  const infoContentClasses = classnames('flex flex-col items-start gap-6', 'md:gap-4', {
    'lg:gap-6': !alternativeLayout,
    'lg:max-w-[394px]': alternativeLayout,
    '!gap-4': smallImage
  });

  const stringToAspectRatio = (aspectRatio: string) => {
    const isValidAspectRatio = /^(\d+):(\d+)$/.test(aspectRatio);
    if (!isValidAspectRatio) {
      return 1;
    }

    const [width, height] = aspectRatio.split(':').map(n => parseInt(n, 10));
    return width / height;
  };

  const imageAspectRatio = stringToAspectRatio(aspectRadioLayout);

  const linkColor = (style?: string) => {
    switch (style) {
      case LinkStyle.Primary:
        return 'black';
      case LinkStyle.Secondary:
        return 'outline-black';
      default:
        return 'outline-black';
    }
  };

  const [isVisible, setIsVisible] = useState(false);

  const handleOnVisible = () => {
    setIsVisible(true);
  };

  useEffect(() => {
    if (isVisible) {
      const nonPromo = {
        block_name: heading ?? '',
        block_type: contentType?.[1] ?? '',
        block_id: contentLink?.id?.toString() ?? ''
      };
      trackViewEditorialEvent(nonPromo);
    }
  }, [contentLink?.id, contentType, heading, isVisible]);

  const handleLinkClick = useCallback(
    (e?: React.MouseEvent) => {
      const nonPromo = {
        block_name: primaryLink ? primaryLink?.title : (secondaryLink?.title ?? ''),
        block_type: primaryLink
          ? primaryLink?.contentType?.[1]
          : (secondaryLink?.contentType?.[1] ?? ''),
        block_id: primaryLink
          ? primaryLink?.contentLink?.id?.toString()
          : (secondaryLink?.contentLink?.id?.toString() ?? '')
      };
      trackSelectEditorialEvent(nonPromo);
      if (onPrimaryButtonClick) {
        e?.preventDefault();
        onPrimaryButtonClick();
      }
    },
    [
      primaryLink,
      secondaryLink?.contentLink?.id,
      secondaryLink?.contentType,
      secondaryLink?.title,
      onPrimaryButtonClick
    ]
  );

  const handleRequestQuoteClick = () => {
    const nonPromo = {
      block_name: requestAQuoteModalBlockProps?.name ?? '',
      block_type: requestAQuoteModalBlockProps?.contentType?.[1] ?? '',
      block_id: requestAQuoteModalBlockProps?.contentLink?.id?.toString() ?? ''
    };
    trackSelectEditorialEvent(nonPromo);
  };

  const renderLink = useCallback(
    (linkItem: LinkItem, propertyName: string, linkStyle: string | undefined) => {
      if (!linkItem && !editMode) {
        return null;
      }

      const classNames = '!w-auto !justify-center';
      const { isAuthenticated, user, signinRedirect } = auth;
      if (requireAuthOnLinks && !isAuthenticated) {
        return (
          <Button
            className={classNames}
            propertyName={propertyName}
            color={linkColor(linkStyle)}
            label={linkItem.text}
            title={linkItem.title}
            size="large"
            onClick={() => {
              signinRedirect({ state: getOIDCState() });
            }}
            iconRight={
              linkItem.target === '_blank' || isExternalUrl(linkItem.href ?? '/')
                ? 'link-out'
                : undefined
            }
          />
        );
      }

      const href = requireAuthOnLinks
        ? linkItem.href?.replace('{individualSk}', user?.profile?.IndividualSK as string)
        : linkItem.href;
      return (
        <Button
          className={classNames}
          propertyName={propertyName}
          color={linkColor(linkStyle)}
          href={link1RenderAsButton ? undefined : href}
          label={linkItem.text}
          target={linkItem.target}
          title={linkItem.title}
          iconRight={
            (linkItem.target === '_blank' || isExternalUrl(linkItem.href ?? '/')) &&
            !link1RenderAsButton
              ? 'link-out'
              : undefined
          }
          size="large"
          onClick={handleLinkClick}
        />
      );
    },
    [auth, editMode, handleLinkClick, requireAuthOnLinks, link1RenderAsButton]
  );

  return (
    <div className="relative">
      <VisibilityObserver onVisible={handleOnVisible}>
        <Block
          contentLinkID={contentLink?.id}
          className={classnames(
            'container module-spacing flex flex-col gap-8 overflow-x-clip bg-white',
            {
              'border-b border-solid border-gray-light px-0': smallImage
            }
          )}
        >
          {outerHeading && (
            <Text propertyName="OuterHeading" element="h3" className="heading-6-medium">
              {outerHeading}
            </Text>
          )}
          <div className={flexClasses}>
            {image || videoID || editMode ? (
              <div className={smallImage ? smallImageClasses : imageColumnClasses}>
                {(!videoID || editMode) && (
                  <ResponsiveImage
                    propertyName="Image"
                    src={image?.expanded?.url ?? image?.url}
                    alt={image?.alt as string}
                    className={classnames('size-full object-cover', {
                      'aspect-square': !videoID,
                      'aspect-video': videoID,
                      'rounded-lg': roundedCorners
                    })}
                    loading="lazy"
                    aspectRatio={imageAspectRatio}
                    imageSizes={{
                      xs: '330px',
                      sm: '500px',
                      md: '768px',
                      lg: '638px'
                    }}
                  />
                )}

                {!editMode && videoID && (
                  <VimeoVideo
                    videoId={videoID}
                    thumbnail={image}
                    title={image?.alt as string}
                    ratio={16 / 9}
                  />
                )}

                {editMode && (
                  <Text propertyName="videoID" className="mt-2">
                    {videoID ? (
                      `Vimeo video ID: ${videoID}`
                    ) : (
                      <span className="font-bold">Add Vimeo video ID</span>
                    )}
                  </Text>
                )}
              </div>
            ) : null}

            <div className={infoColumnClasses}>
              <div className={infoContentClasses}>
                <Image
                  propertyName="Logo"
                  src={logo?.url}
                  alt={logo?.alt as string}
                  className={classnames('h-5')}
                />

                <div className="flex flex-col gap-1">
                  <Text
                    propertyName="EyebrowText"
                    element="span"
                    className={
                      eyebrowSize == 'small'
                        ? classnames('text-gray-dark', {
                            'text-sm font-medium': imageAspectRatio === 1,
                            'text-xs': imageAspectRatio !== 1
                          })
                        : classnames({
                            'heading-6-medium': imageAspectRatio === 1,
                            'text-xs text-gray-dark': imageAspectRatio !== 1
                          })
                    }
                  >
                    {eyebrowText}
                  </Text>

                  <Text
                    propertyName="Heading"
                    element="h2"
                    className={
                      smallImage && !screenSizeXsOnly ? 'heading-3 mt-1' : 'heading-5 mt-1'
                    }
                  >
                    {heading}
                  </Text>
                </div>

                <RichText
                  propertyName="Copy"
                  content={copy || ''}
                  className="text-1 text-gray-dark"
                  checkList={true}
                  checklistRed={checkListRed}
                />
              </div>

              {primaryLink || secondaryLink || editMode || requestAQuoteModalBlockProps ? (
                <div className="flex w-full flex-col items-stretch gap-2 sm:w-fit">
                  {renderLink(primaryLink, 'PrimaryLink', link1Style)}
                  {renderLink(secondaryLink, 'SecondaryLink', link2Style)}
                  {requestAQuoteModalBlockProps ? (
                    <RequestAQuoteModalBlock
                      {...requestAQuoteModalBlockProps}
                      triggerButton={
                        <Button
                          className="w-full !justify-center sm:!w-auto"
                          color={linkColor(link2Style)}
                          label={requestAQuoteModalBlockProps?.heading || ''}
                          size="large"
                          onClick={handleRequestQuoteClick}
                        />
                      }
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </Block>
      </VisibilityObserver>
    </div>
  );
};

export default EditorialToutWithImageBlock;
